import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import {
  fetchWorkerDetail,
  fetchWorksByWorker,
  fetchWorkDetail,
  fetchWorkflowDetail,
  fetchProject,
  fetchProgram,
} from "../../api/memberApi";

const WORK_TYPE_COLORS = {
  ToDo: "default",
  Doing: "primary",
  Done: "success",
};

export default function WorkHistoryPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const notify = useNotification();

  const [worker, setWorker] = useState(null);
  const [works, setWorks] = useState([]);
  const [workflowMap, setWorkflowMap] = useState({});
  const [projectMap, setProjectMap] = useState({});
  const [programMap, setProgramMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedWorkId, setExpandedWorkId] = useState(null);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [workerData, worksData] = await Promise.all([
        fetchWorkerDetail(workerId).catch(() => null),
        fetchWorksByWorker(workerId).catch(() => []),
      ]);
      setWorker(workerData);
      const worksList = Array.isArray(worksData) ? worksData : [];
      setWorks(worksList);

      if (worksList.length > 0) {
        const workDetails = await Promise.all(
          worksList.map((w) => fetchWorkDetail(w.workId).catch(() => null))
        );
        const uniqueWorkflowIds = [
          ...new Set(workDetails.filter((d) => d?.workflowId).map((d) => d.workflowId)),
        ];
        const workflowDetails = await Promise.all(
          uniqueWorkflowIds.map((id) => fetchWorkflowDetail(id).catch(() => null))
        );
        const wfById = {};
        workflowDetails.forEach((wf) => {
          if (wf?.workflowId) wfById[wf.workflowId] = wf;
        });
        const wfByWorkId = {};
        workDetails.forEach((wd) => {
          if (wd?.workId && wd?.workflowId && wfById[wd.workflowId]) {
            wfByWorkId[wd.workId] = wfById[wd.workflowId];
          }
        });
        setWorkflowMap(wfByWorkId);

        // ワークフローごとのプロジェクト/プログラムを個別に取得（ワーカーは複数WFにまたがる可能性あり）
        const uniqueProjectIds = [...new Set(workflowDetails.filter((wf) => wf?.projectId).map((wf) => wf.projectId))];
        const uniqueProgramIds = [...new Set(workflowDetails.filter((wf) => wf?.programId).map((wf) => wf.programId))];
        const [projectResults, programResults] = await Promise.all([
          Promise.all(uniqueProjectIds.map((id) => fetchProject(id).catch(() => null))),
          Promise.all(uniqueProgramIds.map((id) => fetchProgram(id).catch(() => null))),
        ]);
        const pjMap = {};
        projectResults.forEach((p) => { if (p?.projectId) pjMap[p.projectId] = p; });
        const pgMap = {};
        programResults.forEach((p) => { if (p?.programId) pgMap[p.programId] = p; });
        setProjectMap(pjMap);
        setProgramMap(pgMap);
      }
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/workers/${workerId}`, { state: { released: true } })}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: "worker.workHistory.title" })}
          </Typography>
          {worker?.workerName && (
            <Typography variant="body2" color="text.secondary">
              {worker.workerName}
            </Typography>
          )}
        </Box>
        <Chip
          label={intl.formatMessage({ id: "worker.released" })}
          color="default"
          size="small"
        />
      </Box>


      {works.length === 0 ? (
        <Typography color="text.secondary">
          {intl.formatMessage({ id: "worker.workHistory.noWorks" })}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.name" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.status" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.startDate" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {intl.formatMessage({ id: "work.endDate" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {works.map((work) => {
                const wf = workflowMap[work.workId];
                const project = wf?.projectId ? projectMap[wf.projectId] : null;
                const program = wf?.programId ? programMap[wf.programId] : null;
                const hasDetails = wf || work.workDescription;
                return (
                  <React.Fragment key={work.workId}>
                    <TableRow
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setExpandedWorkId(
                          expandedWorkId === work.workId ? null : work.workId
                        )
                      }
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {expandedWorkId === work.workId
                            ? <ExpandLessIcon fontSize="small" color="action" />
                            : <ExpandMoreIcon fontSize="small" color="action" />
                          }
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {work.workName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={work.workType || "—"}
                          color={WORK_TYPE_COLORS[work.workType] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(work.startDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(work.endDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {/* 詳細展開表示 */}
                    {expandedWorkId === work.workId && hasDetails && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0 }}>
                          <Collapse in timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, backgroundColor: "grey.50", borderRadius: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {/* プロジェクト情報 */}
                              {project && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    プロジェクト
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                    {project.projectName}
                                  </Typography>
                                  {project.projectDescription && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                                      {project.projectDescription}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {/* プログラム情報 */}
                              {program && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    プログラム
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                    {program.programName}
                                  </Typography>
                                  {program.programDescription && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                                      {program.programDescription}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {/* ワークフロー情報 */}
                              {wf && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    ワークフロー
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>
                                    {wf.workflowName}
                                  </Typography>
                                  {wf.workflowDescription && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                                      {wf.workflowDescription}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {/* 作業内容 */}
                              {work.workDescription && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {intl.formatMessage({ id: "work.description" })}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                                  >
                                    {work.workDescription}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
